// eslint-disable-next-line import/no-extraneous-dependencies
import * as signalR from '@microsoft/signalr';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { SensorDeviceModel, SensorModel } from 'src/types/dashboardSam';

// Types
interface NotificationMessage {
  title: string;
  body: string;
  caySamId?: string;
  timestamp?: string;
}

interface SensorReading extends SensorDeviceModel {
  timestamp?: string;
}

type NotificationCallback = (notification: NotificationMessage) => void;
type SensorCallback = (sensor: SensorReading) => void;

/**
 * SignalR Singleton Service
 * Đảm bảo chỉ có 1 connection duy nhất trong toàn bộ ứng dụng
 */
class SignalRService {
  private static instance: SignalRService;

  private connection: signalR.HubConnection | null = null;

  private isConnecting: boolean = false;

  private notificationCallbacks: Set<NotificationCallback> = new Set();
  
  private sensorCallbacks: Set<SensorCallback> = new Set();
  
  private constructor() {
    // Private constructor để đảm bảo singleton
  }

  /**
   * Lấy instance duy nhất của service
   */
  public static getInstance(): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  /**
   * Khởi tạo và kết nối SignalR (chỉ chạy 1 lần)
   */
  public async initialize(hubUrl: string, authenticateToken: string): Promise<void> {
    // Nếu đã có connection, không tạo mới
    if (this.connection) {
      console.log('SignalR already initialized');
      return;
    }

    // Nếu đang connecting, đợi
    if (this.isConnecting) {
      console.log('SignalR is connecting...');
      return;
    }

    this.isConnecting = true;
    console.log('Initializing SignalR connection...');

    try {
      const urlWithToken = `${hubUrl}?AuthenticateToken=${authenticateToken}`;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(urlWithToken)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds < 60000) {
              return Math.min(1000 * 2**retryContext.previousRetryCount, 60000);
            }
            return null;
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Đăng ký event handlers
      this.setupEventHandlers();

      // Kết nối
      await this.connection.start();
      console.log('✅ SignalR Connected! Connection ID:', this.connection.connectionId);
      this.isConnecting = false;

    } catch (error) {
      console.error('❌ SignalR Connection Error:', error);
      this.isConnecting = false;
      this.connection = null;
      throw error;
    }
  }

  /**
   * Setup event handlers
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Lắng nghe notifications
    this.connection.on('ReceiveNotification', (message: NotificationMessage) => {
      if (process.env.NODE_ENV === "development") {
      console.log('📩 Received notification:', message);
      }
       
      const notificationWithTimestamp = {
        ...message,
        timestamp: new Date().toISOString(),
      };

      // Gọi tất cả callbacks đã đăng ký
      this.notificationCallbacks.forEach(callback => {
        try {
          callback(notificationWithTimestamp);
        } catch (error) {
          console.error('Error in notification callback:', error);
        }
      });

      // Browser notification
      this.showBrowserNotification(notificationWithTimestamp);
    });
    // Lắng nghe sensor
    this.connection.on('ReceiveSensorReading', (sensor: SensorDeviceModel) => {
      if (process.env.NODE_ENV === "development") {
        console.log("📩 Receive sensor reading:", sensor);
      }
      
      const sensorWithTimestamp: SensorReading = {
        ...sensor,
        timestamp: new Date().toISOString(),
      };

      // Gọi tất cả sensor callbacks đã đăng ký
      this.sensorCallbacks.forEach(callback => {
        try {
          callback(sensorWithTimestamp);
        } catch (error) {
          console.error('Error in sensor callback:', error);
        }
      });
    });

    this.connection.on('receivenotificationlog', (data: any) => {
    // console.log('📋 Received notification log:', data);
    // Handle the notification log data here
    // You can add it to your callbacks or process it differently
    });
    // Reconnecting
    this.connection.onreconnecting((error) => {
      console.log('🔄 Reconnecting...', error);
    });

    // Reconnected
    this.connection.onreconnected((connectionId) => {
      console.log('✅ Reconnected! Connection ID:', connectionId);
    });

    // Close
    this.connection.onclose((error) => {
      console.log('❌ Connection closed', error);
    });
  }

  /**
   * Hiển thị browser notification
   */
  // eslint-disable-next-line class-methods-use-this
  private showBrowserNotification(notification: NotificationMessage): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification(notification.title, {
        body: notification.body,
        icon: '/notification-icon.png',
      });
    }
  }

  /**
   * Đăng ký callback khi nhận notification
   */
  public onNotification(callback: NotificationCallback): () => void {
    this.notificationCallbacks.add(callback);
    
    // Trả về function để unsubscribe
    return () => {
      this.notificationCallbacks.delete(callback);
    };
  }

  /**
   * Đăng ký callback khi nhận sensor reading
   */
  public onSensorReading(callback: SensorCallback): () => void {
    this.sensorCallbacks.add(callback);
    
    // Trả về function để unsubscribe
    return () => {
      this.sensorCallbacks.delete(callback);
    };
  }

  /**
   * Gửi message đến server
   */
  public async invoke(methodName: string, ...args: any[]): Promise<any> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR is not connected');
    }

    try {
      return await this.connection.invoke(methodName, ...args);
    } catch (error) {
      console.error(`Error invoking ${methodName}:`, error);
      throw error;
    }
  }

  /**
   * Kiểm tra trạng thái kết nối
   */
  public isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Lấy connection state
   */
  public getState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  /**
   * Lấy connection instance (cho advanced usage)
   */
  public getConnection(): signalR.HubConnection | null {
    return this.connection;
  }

  /**
   * Request browser notification permission
   */
  // eslint-disable-next-line class-methods-use-this
  public async requestNotificationPermission(): Promise<NotificationPermission> {
    if ('Notification' in window) {
      return Notification.requestPermission();
    }
    return 'denied';
  }

  /**
   * Disconnect (chỉ dùng khi thực sự cần - ví dụ: logout)
   */
  public async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.notificationCallbacks.clear();
      this.sensorCallbacks.clear();
      console.log('SignalR disconnected');
    }
  }
}

// Export singleton instance
export const signalRService = SignalRService.getInstance();
export default signalRService;